/**
 * DSCR Calculator Component
 * Calculates Debt Service Coverage Ratio for rental properties
 */

import React, { useState, useMemo } from "react";
import { Calculator, DollarSign, Home, Percent, Info, CheckCircle, XCircle } from "lucide-react";

function formatCurrency(value) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
}

const loanTerms = [
  { label: "30-Year Fixed", years: 30, rateAdj: 0 },
  { label: "5/1 ARM", years: 30, rateAdj: -0.25 },
  { label: "7/1 ARM", years: 30, rateAdj: -0.125 },
  { label: "10/1 ARM", years: 30, rateAdj: 0 },
  { label: "Interest Only", years: 30, rateAdj: 0.25, interestOnly: true }
];

export default function DSCRCalculator() {
  const [propertyValue, setPropertyValue] = useState(350000);
  const [loanAmount, setLoanAmount] = useState(280000);
  const [monthlyRent, setMonthlyRent] = useState(2800);
  const [interestRate, setInterestRate] = useState(7.5);
  const [loanTermIndex, setLoanTermIndex] = useState(0);
  const [propertyTaxes, setPropertyTaxes] = useState(4200);
  const [insurance, setInsurance] = useState(1800);
  const [hoaFees, setHoaFees] = useState(0);
  const [vacancyRate, setVacancyRate] = useState(5);
  const [managementFee, setManagementFee] = useState(8);

  const calculations = useMemo(() => {
    const term = loanTerms[loanTermIndex];
    const effectiveRate = interestRate + term.rateAdj;
    const monthlyRate = (effectiveRate / 100) / 12;
    const numPayments = term.years * 12;
    
    // Calculate monthly P&I payment
    let monthlyPI;
    if (term.interestOnly) {
      monthlyPI = loanAmount * monthlyRate;
    } else {
      monthlyPI = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                  (Math.pow(1 + monthlyRate, numPayments) - 1);
    }
    
    // Monthly expenses
    const monthlyTaxes = propertyTaxes / 12;
    const monthlyInsurance = insurance / 12;
    const monthlyHoa = hoaFees;
    
    // Total PITIA (Principal, Interest, Taxes, Insurance, Association)
    const totalPITIA = monthlyPI + monthlyTaxes + monthlyInsurance + monthlyHoa;
    
    // Effective rental income (accounting for vacancy)
    const effectiveRent = monthlyRent * (1 - vacancyRate / 100);
    
    // Management fee
    const monthlyManagement = effectiveRent * (managementFee / 100);
    
    // Net Operating Income (NOI)
    const monthlyNOI = effectiveRent - monthlyManagement;
    
    // DSCR Calculation (NOI / Debt Service)
    // For DSCR loans, typically use gross rent / PITIA
    const dscr = monthlyRent / totalPITIA;
    
    // Alternative DSCR with expenses
    const dscrWithExpenses = monthlyNOI / monthlyPI;
    
    // LTV Calculation
    const ltv = (loanAmount / propertyValue) * 100;
    
    // Cash flow
    const monthlyCashFlow = monthlyNOI - totalPITIA;
    const annualCashFlow = monthlyCashFlow * 12;
    
    // Cash on cash return (assuming 20% down)
    const downPayment = propertyValue - loanAmount;
    const closingCosts = loanAmount * 0.03;
    const totalCashInvested = downPayment + closingCosts;
    const cashOnCash = (annualCashFlow / totalCashInvested) * 100;
    
    // Cap Rate
    const annualNOI = monthlyNOI * 12;
    const capRate = (annualNOI / propertyValue) * 100;
    
    // Qualification status
    const qualifies = dscr >= 1.0 && ltv <= 80;
    const dscrStatus = dscr >= 1.25 ? 'excellent' : dscr >= 1.0 ? 'qualifying' : 'below';
    
    return {
      monthlyPI,
      monthlyTaxes,
      monthlyInsurance,
      monthlyHoa,
      totalPITIA,
      effectiveRent,
      monthlyManagement,
      monthlyNOI,
      dscr,
      dscrWithExpenses,
      ltv,
      monthlyCashFlow,
      annualCashFlow,
      cashOnCash,
      capRate,
      qualifies,
      dscrStatus,
      effectiveRate,
      downPayment,
      totalCashInvested
    };
  }, [propertyValue, loanAmount, monthlyRent, interestRate, loanTermIndex, propertyTaxes, insurance, hoaFees, vacancyRate, managementFee]);

  const getDscrColor = () => {
    if (calculations.dscrStatus === 'excellent') return 'text-[#48d480]';
    if (calculations.dscrStatus === 'qualifying') return 'text-[#e8821c]';
    return 'text-red-500';
  };

  const getDscrBg = () => {
    if (calculations.dscrStatus === 'excellent') return 'bg-[#48d480]/10 border-[#48d480]/20';
    if (calculations.dscrStatus === 'qualifying') return 'bg-[#e8821c]/10 border-[#e8821c]/20';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#48d480] to-[#3ab370] p-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
            <Calculator size={24} className="text-white" />
          </div>
          <div>
            <h3 
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              className="text-xl font-bold text-white"
            >
              DSCR Calculator
            </h3>
            <p className="text-white/80 text-sm">Calculate your rental property qualification</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Inputs */}
          <div className="space-y-5">
            <h4 className="font-bold text-[#0f2d52] mb-4">Property & Loan Details</h4>
            
            {/* Property Value */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Property Value
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={propertyValue}
                  onChange={(e) => setPropertyValue(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent"
                />
              </div>
            </div>

            {/* Loan Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Loan Amount
              </label>
              <div className="relative">
                <DollarSign size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={loanAmount}
                  onChange={(e) => setLoanAmount(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                LTV: {calculations.ltv.toFixed(1)}% | Down Payment: {formatCurrency(calculations.downPayment)}
              </p>
            </div>

            {/* Monthly Rent */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Monthly Rent
              </label>
              <div className="relative">
                <Home size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="number"
                  value={monthlyRent}
                  onChange={(e) => setMonthlyRent(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent"
                />
              </div>
            </div>

            {/* Interest Rate & Term */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Interest Rate (%)
                </label>
                <div className="relative">
                  <Percent size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="number"
                    step="0.125"
                    value={interestRate}
                    onChange={(e) => setInterestRate(Number(e.target.value))}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loan Term
                </label>
                <select
                  value={loanTermIndex}
                  onChange={(e) => setLoanTermIndex(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent"
                >
                  {loanTerms.map((term, idx) => (
                    <option key={idx} value={idx}>{term.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Expenses */}
            <div className="pt-4 border-t border-gray-100">
              <h4 className="font-bold text-[#0f2d52] mb-4">Annual Expenses</h4>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Property Taxes
                  </label>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      value={propertyTaxes}
                      onChange={(e) => setPropertyTaxes(Number(e.target.value))}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Insurance
                  </label>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      value={insurance}
                      onChange={(e) => setInsurance(Number(e.target.value))}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    HOA (Monthly)
                  </label>
                  <div className="relative">
                    <DollarSign size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="number"
                      value={hoaFees}
                      onChange={(e) => setHoaFees(Number(e.target.value))}
                      className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vacancy Rate (%)
                  </label>
                  <input
                    type="number"
                    value={vacancyRate}
                    onChange={(e) => setVacancyRate(Number(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#48d480] focus:border-transparent text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="space-y-4">
            <h4 className="font-bold text-[#0f2d52] mb-4">DSCR Analysis</h4>
            
            {/* DSCR Result */}
            <div className={`rounded-xl p-5 border ${getDscrBg()}`}>
              <div className="flex justify-between items-start mb-2">
                <span className="text-sm text-gray-600">Debt Service Coverage Ratio</span>
                <div className="flex items-center gap-2">
                  {calculations.dscr >= 1.0 ? (
                    <CheckCircle size={16} className="text-[#48d480]" />
                  ) : (
                    <XCircle size={16} className="text-red-500" />
                  )}
                  <span className={`text-xs px-2 py-1 rounded font-medium ${
                    calculations.dscrStatus === 'excellent' ? 'bg-[#48d480]/20 text-[#48d480]' :
                    calculations.dscrStatus === 'qualifying' ? 'bg-[#e8821c]/20 text-[#e8821c]' :
                    'bg-red-100 text-red-600'
                  }`}>
                    {calculations.dscrStatus === 'excellent' ? 'Excellent' :
                     calculations.dscrStatus === 'qualifying' ? 'Qualifying' : 'Below Minimum'}
                  </span>
                </div>
              </div>
              <p className={`text-4xl font-black ${getDscrColor()}`}>
                {calculations.dscr.toFixed(2)}x
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Minimum 1.0x required | 1.25x+ preferred for best rates
              </p>
            </div>

            {/* Monthly Payment Breakdown */}
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
              <p className="text-sm font-medium text-gray-700 mb-3">Monthly Payment (PITIA)</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Principal & Interest</span>
                  <span className="text-gray-700">{formatCurrency(calculations.monthlyPI)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Property Taxes</span>
                  <span className="text-gray-700">{formatCurrency(calculations.monthlyTaxes)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Insurance</span>
                  <span className="text-gray-700">{formatCurrency(calculations.monthlyInsurance)}</span>
                </div>
                {calculations.monthlyHoa > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">HOA</span>
                    <span className="text-gray-700">{formatCurrency(calculations.monthlyHoa)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm pt-2 border-t border-gray-200">
                  <span className="text-gray-700 font-medium">Total PITIA</span>
                  <span className="text-gray-900 font-bold">{formatCurrency(calculations.totalPITIA)}</span>
                </div>
              </div>
            </div>

            {/* Cash Flow */}
            <div className={`rounded-xl p-4 border ${calculations.monthlyCashFlow >= 0 ? 'bg-[#48d480]/10 border-[#48d480]/20' : 'bg-red-50 border-red-200'}`}>
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm font-medium text-gray-700">Monthly Cash Flow</span>
                <span className={`text-xl font-bold ${calculations.monthlyCashFlow >= 0 ? 'text-[#48d480]' : 'text-red-500'}`}>
                  {formatCurrency(calculations.monthlyCashFlow)}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Annual Cash Flow</span>
                  <p className="font-medium text-[#0f2d52]">{formatCurrency(calculations.annualCashFlow)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Cash on Cash Return</span>
                  <p className={`font-medium ${calculations.cashOnCash >= 0 ? 'text-[#48d480]' : 'text-red-500'}`}>
                    {calculations.cashOnCash.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <span className="text-xs text-gray-500">LTV</span>
                <p className={`font-bold ${calculations.ltv <= 75 ? 'text-[#48d480]' : calculations.ltv <= 80 ? 'text-[#e8821c]' : 'text-red-500'}`}>
                  {calculations.ltv.toFixed(1)}%
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                <span className="text-xs text-gray-500">Cap Rate</span>
                <p className="font-bold text-[#0f2d52]">{calculations.capRate.toFixed(2)}%</p>
              </div>
            </div>

            {/* Qualification Status */}
            <div className={`rounded-xl p-4 border ${calculations.qualifies ? 'bg-[#48d480]/10 border-[#48d480]/20' : 'bg-yellow-50 border-yellow-200'}`}>
              <div className="flex items-center gap-3">
                {calculations.qualifies ? (
                  <CheckCircle size={24} className="text-[#48d480]" />
                ) : (
                  <Info size={24} className="text-yellow-600" />
                )}
                <div>
                  <p className={`font-bold ${calculations.qualifies ? 'text-[#48d480]' : 'text-yellow-700'}`}>
                    {calculations.qualifies ? 'Likely Qualifies!' : 'May Need Adjustment'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {calculations.qualifies 
                      ? 'This property meets typical DSCR loan requirements'
                      : `DSCR: ${calculations.dscr.toFixed(2)}x (min 1.0x) | LTV: ${calculations.ltv.toFixed(0)}% (max 80%)`}
                  </p>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="flex items-start gap-2 text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
              <Info size={14} className="flex-shrink-0 mt-0.5" />
              <span>
                This calculator provides estimates only. Actual rates, terms, and qualification requirements may vary. 
                Contact us for a personalized quote.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
